import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface ConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description: string
  confirmButtonText?: string
  cancelButtonText?: string
  secondaryActionText?: string
  onSecondaryAction?: () => void
  children?: React.ReactNode
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmButtonText = 'Delete',
  cancelButtonText = 'Cancel',
  secondaryActionText,
  onSecondaryAction,
  children,
}) => {
  const [isConfirming, setIsConfirming] = useState<boolean>(false)

  const handleConfirm = async () => {
    const result = onConfirm()
    if (result != null && typeof (result as Promise<unknown>)?.then === 'function') {
      setIsConfirming(true)
      try {
        await (result as Promise<unknown>)
        onClose()
      } catch {
        // Leave modal open on error; caller can show toast
      } finally {
        setIsConfirming(false)
      }
    } else {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isConfirming && onClose()}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter className='flex gap-2 sm:justify-end'>
          <Button
            type='button'
            variant='outline'
            onClick={onClose}
            disabled={isConfirming}
            className='w-full sm:w-auto'
          >
            {cancelButtonText}
          </Button>

          {secondaryActionText && onSecondaryAction && (
            <Button
              type='button'
              variant='secondary'
              onClick={() => {
                onSecondaryAction()
                onClose()
              }}
              disabled={isConfirming}
              className='w-full sm:w-auto'
            >
              {secondaryActionText}
            </Button>
          )}

          <Button
            type='button'
            variant='destructive'
            onClick={handleConfirm}
            disabled={isConfirming}
            className='w-full sm:w-auto'
          >
            {confirmButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ConfirmationModal
